import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectAttachments, selectRails } from '../../../store/selectors/databaseSelectors';
import { addAttachment, addRail } from '../../../store/slices/databaseSlice';
import { attachmentsService, railsService } from '../../../services';
import { Attachment, Rail, DomesticStatus } from '../../../types';
import { TOAST_CONFIG, TOAST_WARNING_CONFIG } from '../../../constants';
import { SearchDropdown } from '../../../components';
import toast from 'react-hot-toast';

interface AttachmentForm {
  name: string;
}

interface RailForm {
  name: string;
  domestic_status: DomesticStatus;
  length: number | null;
  attachmentIds: string[];
}

type ActiveTab = 'attachments' | 'rails';

const TABS = [
  { id: 'attachments' as const, label: 'Attachments', description: 'Mounting hardware and attachment points' },
  { id: 'rails' as const, label: 'Rails', description: 'Rail systems and their compatible attachments' }
];

// Domestic status options for dropdown
const DOMESTIC_STATUS_OPTIONS = [
  { id: DomesticStatus.DOM, label: 'DOM' },
  { id: DomesticStatus.NON_DOM, label: 'NON_DOM' }
];

export const AttachmentRailManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  
  // Get data from Redux
  const attachments = useAppSelector(selectAttachments);
  const rails = useAppSelector(selectRails);
  
  // Form states
  const [attachmentForm, setAttachmentForm] = useState<AttachmentForm>({
    name: ''
  });
  
  const [railForm, setRailForm] = useState<RailForm>({
    name: '',
    domestic_status: DomesticStatus.DOM,
    length: null,
    attachmentIds: []
  });
  
  // UI states
  const [activeTab, setActiveTab] = useState<ActiveTab>('attachments');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localAttachments, setLocalAttachments] = useState<Attachment[]>(attachments);
  const [localRails, setLocalRails] = useState<Rail[]>(rails);
  const [selectedDomesticStatusOption, setSelectedDomesticStatusOption] = useState<{id: string, label: string} | null>(
    DOMESTIC_STATUS_OPTIONS[0] // Default to DOM
  );

  // Initialize local state with Redux data
  useEffect(() => {
    setLocalAttachments(attachments);
  }, [attachments]);

  useEffect(() => {
    setLocalRails(rails);
  }, [rails]);

  const handleAttachmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!attachmentForm.name.trim()) {
      toast('Please enter an attachment name', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      const newAttachment = await attachmentsService.createAttachment({
        name: attachmentForm.name.trim()
      });
      
      // Update Redux state
      dispatch(addAttachment(newAttachment));
      
      // Update local state - prepend new item
      setLocalAttachments(prev => [newAttachment, ...prev]);
      
      // Reset form
      setAttachmentForm({ name: '' });
      
      toast.success('Attachment created successfully!', TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating attachment:', error);
      toast.error('Failed to create attachment. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!railForm.name.trim()) {
      toast('Please enter a rail name', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      const newRail = await railsService.createRail({
        name: railForm.name.trim(),
        domesticStatus: railForm.domestic_status,
        length: railForm.length || undefined,
        attachmentIds: railForm.attachmentIds.length > 0 ? railForm.attachmentIds : undefined
      });

      // Update Redux state
      dispatch(addRail(newRail));

      // Update local state - prepend new item
      setLocalRails(prev => [newRail, ...prev]);

      // Reset form
      setRailForm({ name: '', domestic_status: DomesticStatus.DOM, length: null, attachmentIds: [] });
      setSelectedDomesticStatusOption(DOMESTIC_STATUS_OPTIONS[0]);
      
      toast.success('Rail created successfully!', TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating rail:', error);
      toast.error('Failed to create rail. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle domestic status selection
  const handleDomesticStatusSelect = (option: {id: string, label: string} | null) => {
    setSelectedDomesticStatusOption(option);
    setRailForm(prev => ({ ...prev, domestic_status: (option?.id as DomesticStatus) || DomesticStatus.DOM }));
  };

  // Get attachments for rail (if any)
  const getRailAttachments = (rail: Rail) => {
    return rail.attachments || [];
  };

  return (
    <div className="p-4">
      {/* Tabs */}
      <div className="mb-6 border-b border-slate-200">
        <div className="flex flex-wrap gap-2 -mb-px">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const count = tab.id === 'attachments' ? localAttachments.length : localRails.length;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-4 py-2.5 text-sm font-medium rounded-t-lg transition-all duration-200
                  ${isActive 
                    ? 'bg-white border-t border-x border-slate-200 text-slate-900 -mb-px' 
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }
                `}
              >
                <div className="flex items-center gap-2">
                  <span>{tab.label}</span>
                  <span className={`
                    text-xs px-1.5 py-0.5 rounded-full font-normal
                    ${isActive 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-slate-100 text-slate-600'
                    }
                  `}>
                    {count}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Form Section */}
        <div className="bg-white rounded-lg p-5 border border-slate-200 shadow-sm">
          <div className="mb-4">
            <h3 className="text-base font-semibold text-slate-900">
              Add New {activeTab === 'attachments' ? 'Attachment' : 'Rail'}
            </h3>
            <p className="text-xs text-slate-600 mt-1">
              {TABS.find(t => t.id === activeTab)?.description}
            </p>
          </div>

          {activeTab === 'attachments' ? (
            <form onSubmit={handleAttachmentSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Attachment Name *
                </label>
                <input
                  type="text"
                  value={attachmentForm.name}
                  onChange={(e) => setAttachmentForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., L-Foot, Tile Hook, Comp Shingle Hook"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting || !attachmentForm.name.trim()}
                className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isSubmitting ? 'Creating...' : 'Add Attachment'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRailSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Rail Name *
                </label>
                <input
                  type="text"
                  value={railForm.name}
                  onChange={(e) => setRailForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., IronRidge XR-1000, SolarMount SM-1, Unirac SolarMount"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">
                    Domestic Status *
                  </label>
                  <SearchDropdown
                    options={DOMESTIC_STATUS_OPTIONS}
                    value={selectedDomesticStatusOption}
                    onSelect={handleDomesticStatusSelect}
                    placeholder="Select status"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">
                    Length (mm)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={railForm.length || ''}
                    onChange={(e) => setRailForm(prev => ({ ...prev, length: parseInt(e.target.value) || null }))}
                    placeholder="e.g., 168"
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Compatible Attachments (Optional)
                </label>
                <div className="border border-slate-300 rounded-md p-2 max-h-32 overflow-y-auto">
                  {localAttachments.length > 0 ? (
                    localAttachments.map((attachment) => (
                      <label key={attachment.id} className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded">
                        <input
                          type="checkbox"
                          checked={railForm.attachmentIds.includes(attachment.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setRailForm(prev => ({ 
                                ...prev, 
                                attachmentIds: [...prev.attachmentIds, attachment.id] 
                              }));
                            } else {
                              setRailForm(prev => ({ 
                                ...prev, 
                                attachmentIds: prev.attachmentIds.filter(id => id !== attachment.id) 
                              }));
                            }
                          }}
                          className="text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                        />
                        <span className="text-sm text-slate-700">{attachment.name}</span>
                      </label>
                    ))
                  ) : (
                    <p className="text-xs text-slate-500 text-center py-2">No attachments available</p>
                  )}
                </div>
                {railForm.attachmentIds.length > 0 && (
                  <p className="text-xs text-slate-600 mt-1">
                    {railForm.attachmentIds.length} attachment{railForm.attachmentIds.length > 1 ? 's' : ''} selected
                  </p>
                )}
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting || !railForm.name.trim()}
                className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isSubmitting ? 'Creating...' : 'Add Rail'}
              </button>
            </form>
          )}
        </div>

        {/* List Section */}
        <div className="bg-white rounded-lg p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-slate-900">
              Existing {activeTab === 'attachments' ? 'Attachments' : 'Rails'}
            </h3>
            <span className="text-xs text-slate-500">
              {activeTab === 'attachments' ? localAttachments.length : localRails.length} items
            </span>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {activeTab === 'attachments' ? (
              localAttachments.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {localAttachments.map((attachment) => (
                    <div 
                      key={attachment.id} 
                      className="inline-flex items-center px-3 py-1.5 rounded-full border bg-blue-50 text-blue-800 border-blue-200 hover:shadow-sm transition-shadow"
                    >
                      <span className="text-sm font-medium">{attachment.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-slate-400 mb-2">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <p className="text-slate-500 text-sm">No attachments configured yet</p>
                  <p className="text-slate-400 text-xs mt-1">Add your first attachment using the form</p>
                </div>
              )
            ) : (
              localRails.length > 0 ? (
                <div className="space-y-2">
                  {localRails.map((rail) => {
                    const attachments = getRailAttachments(rail);
                    return (
                      <div 
                        key={rail.id} 
                        className="p-3 rounded-lg border bg-green-50 border-green-200 hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-green-800">{rail.name}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-green-600">
                                {(rail.domestic_status || (rail as any).domesticStatus) === DomesticStatus.DOM ? 'DOM' : 'NON_DOM'}
                              </span>
                              {rail.length && (
                                <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                                  {rail.length}"
                                </span>
                              )}
                            </div>
                          </div>
                          {attachments.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {attachments.slice(0, 2).map((attachment) => (
                                <span key={attachment.id} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                                  {attachment.name}
                                </span>
                              ))}
                              {attachments.length > 2 && (
                                <span className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded-full">
                                  +{attachments.length - 2}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-slate-400 mb-2">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </div>
                  <p className="text-slate-500 text-sm">No rails configured yet</p>
                  <p className="text-slate-400 text-xs mt-1">Add your first rail using the form</p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};